import { Icons } from "../Icons";
import { EmptyState } from "@/components/EmptyState";
import { useApi } from "@/lib/hooks";
import { post } from "@/lib/api";

interface ApiNotif {
  id: number | string;
  source?: "jcontrol" | "support";
  icon?: string;
  title?: string;
  message?: string;
  body?: string;
  subtitle?: string;
  group?: string;
  bucket?: string;
  read?: boolean;
  unread?: boolean;
  read_at?: string | null;
  created_at?: string;
  severity?: string;
  priority?: string;
}

const ICON_MAP: Record<string, keyof typeof Icons> = {
  alert: "AlertTri",
  warning: "AlertTri",
  ticket: "Ticket",
  truck: "Truck",
  drop: "Drop",
  water: "Drop",
  bell: "Bell",
  info: "Info",
  check: "Check",
  form: "Form",
  sparkle: "Sparkle",
};

function pickIcon(name?: string): keyof typeof Icons {
  if (!name) return "Bell";
  return ICON_MAP[name.toLowerCase()] ?? "Bell";
}

function colourFor(severity?: string): string {
  const v = (severity ?? "").toLowerCase();
  if (v === "critical" || v === "alert" || v === "high") return "var(--terracotta)";
  if (v === "medium" || v === "warning") return "var(--amber)";
  if (v === "info") return "var(--info)";
  return "var(--green-500)";
}

function bucketOf(n: ApiNotif): string {
  if (n.group) return n.group;
  if (n.bucket) return n.bucket;
  if (!n.created_at) return "Earlier";
  const created = new Date(n.created_at);
  const now = new Date();
  if (created.toDateString() === now.toDateString()) return "Today";
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (created.toDateString() === yesterday.toDateString()) return "Yesterday";
  return "Earlier";
}

function relTime(iso?: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const diff = Date.now() - d.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

export default function NotificationsPanel({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { data, loading, error, reload } =
    useApi<{ data?: ApiNotif[]; notifications?: ApiNotif[] }>(open ? "/v1/jcontrol/notifications" : null);
  // Support writes to Laravel's notifications table, which the bell never
  // read, so ticket replies and assignments were invisible (SUP-05).
  const { data: supportData, reload: reloadSupport } =
    useApi<{ data?: ApiNotif[] }>(open ? "/v1/support/notifications" : null);

  const reloadAll = () => { reload(); reloadSupport(); };
  // "Mark all read" had no handler and items were never marked read, so the
  // unread state was permanent (FE-27). post() also emits data:changed, which
  // refreshes the bell's unread count in the shell.
  const markAllRead = () => {
    void Promise.allSettled([
      post("/v1/jcontrol/notifications/mark-all-read"),
      post("/v1/support/notifications/read-all"),
    ]).then(reloadAll);
  };
  const markRead = (n: ApiNotif) => {
    if (n.read_at != null) return;
    const url = n.source === "support"
      ? `/v1/support/notifications/${n.id}/read`
      : `/v1/jcontrol/notifications/${n.id}/read`;
    void post(url).then(reloadAll).catch(() => {});
  };

  if (!open) return null;

  const items = [
    ...((data?.data ?? data?.notifications ?? []) as ApiNotif[]).map((n) => ({ ...n, source: "jcontrol" as const })),
    ...(supportData?.data ?? []).map((n) => ({ ...n, source: "support" as const })),
  ].sort((a, b) => (b.created_at ?? "").localeCompare(a.created_at ?? ""));
  const unreadCount = items.filter((n) => n.unread === true || (n.read === false) || n.read_at == null).length;

  // Group items into ordered buckets.
  const buckets: { name: string; items: ApiNotif[] }[] = [];
  for (const n of items) {
    const b = bucketOf(n);
    let bucket = buckets.find((x) => x.name === b);
    if (!bucket) {
      bucket = { name: b, items: [] };
      buckets.push(bucket);
    }
    bucket.items.push(n);
  }

  return (
    <>
      <div className="scrim" onClick={onClose} style={{ background: "transparent" }} />
      <div style={{
        position: "fixed", right: 16, top: 64, width: 380, maxHeight: "calc(100vh - 80px)",
        background: "var(--warm-card)", border: "1px solid var(--warm-border)",
        borderRadius: 14, boxShadow: "var(--shadow-xl)", zIndex: 1001,
        display: "flex", flexDirection: "column", animation: "popIn 200ms",
      }}>
        <div className="row" style={{ padding: "14px 16px", borderBottom: "1px solid var(--warm-border)" }}>
          <h3 style={{ fontSize: 15 }}>Notifications</h3>
          {unreadCount > 0 && <span className="pill pill-pending" style={{ marginLeft: 8 }}>{unreadCount} new</span>}
          <button className="btn btn-ghost btn-sm" style={{ marginLeft: "auto" }} onClick={markAllRead}
                  disabled={unreadCount === 0}>Mark all read</button>
        </div>
        <div className="row" style={{ padding: "6px 8px", borderBottom: "1px solid var(--warm-border)", gap: 0 }}>
          {["All", "Mentions", "Alerts", "System"].map((t, i) => (
            <button key={t} className="tab" style={{
              borderRadius: 0,
              borderBottom: i === 0 ? "2px solid var(--green-500)" : "2px solid transparent",
              height: 32,
              color: i === 0 ? "var(--warm-text)" : "var(--warm-text-muted)",
              background: "transparent", boxShadow: "none",
            }}>{t}</button>
          ))}
        </div>
        <div style={{ flex: 1, overflowY: "auto" }}>
          {loading ? (
            <EmptyState icon="Bell" title="Loading…" />
          ) : error ? (
            <EmptyState icon="AlertTri" title="Couldn't load notifications" />
          ) : items.length === 0 ? (
            <EmptyState icon="Bell" title="You're all caught up" body="New notifications will appear here." />
          ) : (
            buckets.map((g) => (
              <div key={g.name}>
                <div className="kicker" style={{ padding: "10px 16px 4px", fontSize: 10 }}>{g.name}</div>
                {g.items.map((it) => {
                  const I = Icons[pickIcon(it.icon)];
                  const isUnread = it.unread === true || it.read === false || it.read_at == null;
                  const title = it.title ?? it.message ?? it.body ?? "Notification";
                  const sub = it.subtitle ?? relTime(it.created_at);
                  return (
                    <div key={`${it.source}-${it.id}`} className="row" onClick={() => markRead(it)} style={{
                      padding: "10px 16px", gap: 12,
                      borderTop: "1px solid var(--warm-border)", cursor: isUnread ? "pointer" : "default",
                      background: isUnread ? "oklch(0.98 0.02 145)" : "transparent",
                    }}>
                      <div style={{
                        width: 32, height: 32, borderRadius: 10,
                        background: "var(--warm-canvas)", color: colourFor(it.severity ?? it.priority),
                        display: "grid", placeItems: "center", flex: "0 0 auto",
                      }}><I size={15} /></div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: isUnread ? 600 : 500 }}>{title}</div>
                        {sub && <div className="muted" style={{ fontSize: 11.5 }}>{sub}</div>}
                      </div>
                      {isUnread && <span style={{ width: 7, height: 7, borderRadius: "50%", background: "var(--green-500)" }} />}
                    </div>
                  );
                })}
              </div>
            ))
          )}
        </div>
        <div style={{ padding: "10px 16px", borderTop: "1px solid var(--warm-border)", textAlign: "center" }}>
          <button className="btn btn-ghost btn-sm">View all notifications</button>
        </div>
      </div>
    </>
  );
}
